import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  ResponsiveContainer,
  AreaChart,
  XAxis,
  YAxis,
  Area,
  Tooltip,
} from 'recharts';
import { LeftCircleTwoTone, RightCircleTwoTone } from '@ant-design/icons';
import { getElapsedHoursInCurrentMonth } from '../../utils/timeUtils';

function MonthlyProductionChart({
  productionData,
  year,
  onYearChange,
  integerOnly = false,
  capacityMW = null,
}) {
  // Format data for the chart
  const chartData = productionData[year] || [];

  // Get previous and next years that have data
  const years = Object.keys(productionData).sort();
  const currentIndex = years.indexOf(String(year));
  const previousYear =
    currentIndex > 0 ? Number(years[currentIndex - 1]) : null;
  const nextYear =
    currentIndex < years.length - 1 ? Number(years[currentIndex + 1]) : null;

  // Auto-derive unit based on magnitude (MWh vs GWh)
  const maxVal = chartData.reduce(
    (m, d) => Math.max(m, Number(d.production) || 0),
    0,
  );
  const useGWh = maxVal >= 1000;
  const formatValue = v => {
    const n = Number(v) || 0;
    const scaled = useGWh ? n / 1000 : n;
    return integerOnly ? Math.round(scaled) : Math.round(scaled * 10) / 10;
  };
  const unit = useGWh ? 'GWh' : 'MWh';

  // Custom tooltip with monthly load and yearly stats
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const month = label;
    const monthProd = Number(payload[0].value) || 0; // MWh
    const days = new Date(year, month, 0).getDate();
    const totalHours = days * 24;

    // For current month, use elapsed hours for more accurate calculations
    const now = moment();
    const isCurrentMonth = now.year() === year && now.month() + 1 === month;
    const effectiveHours = isCurrentMonth
      ? getElapsedHoursInCurrentMonth(year, month)
      : totalHours;

    const monthLoad =
      capacityMW && capacityMW > 0 && effectiveHours > 0
        ? Math.round((monthProd / (capacityMW * effectiveHours)) * 100)
        : null;
    const monthAvgPowerMW = effectiveHours > 0 ? monthProd / effectiveHours : 0;

    const fmtEnergy = mwh =>
      mwh >= 1000 ? `${Math.round(mwh / 1000)} GWh` : `${Math.round(mwh)} MWh`;
    const fmtPower = mw =>
      mw >= 1000 ? `${(mw / 1000).toFixed(2)} GW` : `${Math.round(mw)} MW`;

    return (
      <div
        className="MonthlyProductionChart__tooltip"
        style={{ background: '#fff', padding: '8px', border: '1px solid #ddd' }}
      >
        <div>
          <strong>
            {new Date(0, month - 1).toLocaleString('default', {
              month: 'long',
            })}{' '}
            {year}
          </strong>
        </div>
        <div>Monthly power: {fmtPower(monthAvgPowerMW)}</div>
        {monthLoad !== null && <div>Monthly load: {monthLoad}%</div>}
        <div>Monthly production: {fmtEnergy(monthProd)}</div>
        {isCurrentMonth && effectiveHours < totalHours && (
          <div style={{ fontSize: '12px', color: '#666' }}>
            *Adjusted for {Math.round(effectiveHours)} elapsed hours
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="MonthlyProductionChart">
      <div className="MonthlyProductionChart__navigation">
        {previousYear && (
          <LeftCircleTwoTone
            style={{ fontSize: '18pt', cursor: 'pointer' }}
            onClick={() => onYearChange && onYearChange(previousYear)}
          />
        )}
        <span>{year}</span>
        {nextYear && (
          <RightCircleTwoTone
            style={{ fontSize: '18pt', cursor: 'pointer' }}
            onClick={() => onYearChange && onYearChange(nextYear)}
          />
        )}
      </div>
      <div className="MonthlyProductionChart__chart">
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={chartData}>
            <XAxis
              dataKey="month"
              tickFormatter={month =>
                new Date(0, month - 1).toLocaleString('default', {
                  month: 'short',
                })
              }
            />
            <YAxis
              tickFormatter={v => formatValue(v)}
              label={{ value: unit, angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="production"
              stroke="#8884d8"
              fill="#8884d8"
              fillOpacity={0.3}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

MonthlyProductionChart.propTypes = {
  productionData: PropTypes.object.isRequired,
  year: PropTypes.number.isRequired,
  onYearChange: PropTypes.func,
  integerOnly: PropTypes.bool,
  capacityMW: PropTypes.number,
};

export default MonthlyProductionChart;
